import type { KeyboardEvent } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { apiFetch } from '@/api/client'
import { streamTurn, type StreamHandle } from '@/api/sse'
import type {
  PublicConfig,
  PublicServer,
  SessionResponse,
  StreamEvent,
  TranscriptEventRecord,
  TranscriptResponse,
} from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import {
  clearActiveSession,
  loadActiveSession,
  saveActiveSession,
} from '@/lib/session-store'
import { AppShell } from '@/ui/AppShell'
import { SidePanel, StampFlourish, StatusDot } from '@/ui/primitives'
import { Rolodex, type RolodexItem } from '@/ui/Rolodex'

import { LiveFeed } from './LiveFeed'
import { SessionList } from './SessionList'
import { TurnView } from './TurnView'
import {
  eventsToTranscript,
  eventsToLiveFeed,
  findActiveModelFromEvents,
  groupTurns,
  normalizeToolResultParts,
  summarizeToolResultParts,
  turnKey,
  type TranscriptEntry,
} from './transcript'

function isSessionRunning(session: SessionResponse): boolean {
  return session.turn_state === 'running'
}

const TITLE_REFRESH_DELAY_MS = 1000
const TITLE_REFRESH_ATTEMPTS = 30
const POLL_INTERVAL_MS = 1500
const FEED_OPEN_KEY = 'remote-ai-live-feed-open'

function hasSessionTitle(session: SessionResponse | null): boolean {
  return Boolean(session?.title?.trim())
}

function loadFeedPreference(): boolean {
  try {
    const raw = localStorage.getItem(FEED_OPEN_KEY)
    if (raw === '0') return false
    if (raw === '1') return true
  } catch {
    // ignore disabled storage
  }
  return true
}

function runtimeAgeMs(session: SessionResponse): number | null {
  const stamp = session.last_progress_at || session.turn_started_at
  if (!stamp) return null
  return Date.now() - new Date(stamp).getTime()
}

function runtimeStatusText(session: SessionResponse | null): string | undefined {
  if (!session) return undefined
  if (session.turn_state === 'stalled' || session.status === 'broken') {
    return session.last_stall_reason || 'stalled'
  }
  if (session.turn_state !== 'running') return undefined
  const age = runtimeAgeMs(session)
  const longRunning = age !== null && age >= 60_000
  switch (session.turn_phase) {
    case 'waiting_for_model':
      return longRunning ? 'waiting for model (long)…' : 'waiting for model…'
    case 'streaming_model':
      return longRunning ? 'streaming model output (long)…' : 'streaming model output…'
    case 'running_tool':
      return session.current_tool_name === 'mcp__fleet__remote_exec'
        ? longRunning
          ? 'remote command still running (long)…'
          : 'running remote command…'
        : longRunning
          ? 'running tool (long)…'
          : 'running tool…'
    case 'finalizing':
      return longRunning ? 'finalizing (long)…' : 'finalizing…'
    case 'starting':
      return 'starting…'
    default:
      return longRunning ? 'working (long)…' : 'working…'
  }
}

export function ChatPage() {
  const { user } = useAuth()
  const [servers, setServers] = useState<PublicServer[]>([])
  const [serverPick, setServerPick] = useState<number | null>(null)
  const [sessions, setSessions] = useState<SessionResponse[]>([])
  const [session, setSession] = useState<SessionResponse | null>(null)
  const [entries, setEntries] = useState<TranscriptEntry[]>([])
  const [auditEvents, setAuditEvents] = useState<TranscriptEventRecord[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [streamingSince, setStreamingSince] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [config, setConfig] = useState<PublicConfig | null>(null)
  const [activeModel, setActiveModel] = useState<string>('')
  const [openDetailsTurns, setOpenDetailsTurns] = useState<Set<string>>(new Set())
  const [rolodexOpen, setRolodexOpen] = useState(false)
  const [feedOpen, setFeedOpen] = useState<boolean>(() => loadFeedPreference())
  const [feedPanelOpen, setFeedPanelOpen] = useState(false)
  const [sessionsDrawerOpen, setSessionsDrawerOpen] = useState(false)
  const [stampKey, setStampKey] = useState<number | null>(null)
  const streamRef = useRef<StreamHandle | null>(null)
  const pollRef = useRef<number | null>(null)
  const titlePollRef = useRef<number | null>(null)
  const sessionRef = useRef<SessionResponse | null>(null)
  const entriesRef = useRef<TranscriptEntry[]>([])
  const auditEventsRef = useRef<TranscriptEventRecord[]>([])
  const streamingRef = useRef(false)
  const streamAbortExpectedRef = useRef(false)
  const pollBusyRef = useRef(false)
  const endRef = useRef<HTMLDivElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  const feedItems = useMemo(() => eventsToLiveFeed(auditEvents), [auditEvents])

  useEffect(() => {
    sessionRef.current = session
  }, [session])

  useEffect(() => {
    entriesRef.current = entries
  }, [entries])

  useEffect(() => {
    auditEventsRef.current = auditEvents
  }, [auditEvents])

  useEffect(() => {
    streamingRef.current = streaming
  }, [streaming])

  function setFeedOpenPersisted(next: boolean) {
    setFeedOpen(next)
    try {
      localStorage.setItem(FEED_OPEN_KEY, next ? '1' : '0')
    } catch {
      // ignore disabled storage
    }
  }

  function abortActiveStream() {
    streamAbortExpectedRef.current = true
    streamRef.current?.abort()
    streamRef.current = null
  }

  function applyTranscript(
    payload: TranscriptResponse,
    primaryModel: string,
    options?: { resetDetails?: boolean },
  ) {
    setSession(payload.session)
    setAuditEvents(payload.events)
    setEntries(eventsToTranscript(payload.events))
    setActiveModel(findActiveModelFromEvents(payload.events, primaryModel))
    if (options?.resetDetails !== false) {
      setOpenDetailsTurns(new Set())
    }
  }

  function shouldIgnoreStaleTranscript(payload: TranscriptResponse, sessionId: string): boolean {
    if (!streamingRef.current) return false
    if (sessionRef.current?.session_id !== sessionId) return false
    if (isSessionRunning(payload.session)) return false

    const currentAuditCount = auditEventsRef.current.length
    if (payload.events.length < currentAuditCount) return true

    return (
      payload.events.length === 0 &&
      entriesRef.current.some((entry) => entry.kind === 'user')
    )
  }

  async function syncTranscript(
    sessionId: string,
    primaryModel: string,
    options?: { resetDetails?: boolean; refreshListOnIdle?: boolean },
  ): Promise<TranscriptResponse | null> {
    try {
      const payload = await apiFetch<TranscriptResponse>(`/v1/sessions/${sessionId}`)
      if (sessionRef.current?.session_id && sessionRef.current?.session_id !== sessionId) {
        return payload
      }
      if (shouldIgnoreStaleTranscript(payload, sessionId)) {
        return payload
      }
      applyTranscript(payload, primaryModel, { resetDetails: options?.resetDetails })
      if (!isSessionRunning(payload.session)) {
        stopPolling()
        setStreaming(false)
        setStreamingSince(null)
        if (options?.refreshListOnIdle) void refreshSessions()
      }
      return payload
    } catch {
      return null
    }
  }

  function stopPolling() {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  function stopTitlePolling() {
    if (titlePollRef.current !== null) {
      window.clearTimeout(titlePollRef.current)
      titlePollRef.current = null
    }
  }

  function startPolling(
    sid: string,
    primaryModel: string,
    options?: { immediate?: boolean },
  ) {
    stopPolling()
    const tick = async () => {
      if (pollBusyRef.current) return
      pollBusyRef.current = true
      try {
        await syncTranscript(sid, primaryModel, {
          resetDetails: false,
          refreshListOnIdle: true,
        })
      } finally {
        pollBusyRef.current = false
      }
    }
    if (options?.immediate !== false) {
      void tick()
    }
    pollRef.current = window.setInterval(() => {
      void tick()
    }, POLL_INTERVAL_MS)
  }

  function refreshTitleSoon(sid: string, primaryModel: string, attempt = 0) {
    if (attempt >= TITLE_REFRESH_ATTEMPTS) return
    stopTitlePolling()
    titlePollRef.current = window.setTimeout(async () => {
      try {
        const current = sessionRef.current
        if (!current || current.session_id !== sid || hasSessionTitle(current)) return
        const r = await syncTranscript(sid, primaryModel, { resetDetails: false })
        if (!r || sessionRef.current?.session_id !== sid) return
        await refreshSessions()
        if (!hasSessionTitle(r.session)) refreshTitleSoon(sid, primaryModel, attempt + 1)
      } catch {
        refreshTitleSoon(sid, primaryModel, attempt + 1)
      }
    }, TITLE_REFRESH_DELAY_MS)
  }

  async function refreshSessions() {
    try {
      const list = await apiFetch<SessionResponse[]>('/v1/sessions')
      setSessions(list)
      setSession((prev) => {
        if (!prev) return prev
        const next = list.find((item) => item.session_id === prev.session_id)
        if (!next) return prev
        return next.title && next.title !== prev.title
          ? { ...prev, title: next.title }
          : prev
      })
    } catch {
      // ignore
    }
  }

  async function loadAndAttach(
    sessionId: string,
    primaryModel: string,
  ): Promise<boolean> {
    try {
      stopTitlePolling()
      const r = await apiFetch<TranscriptResponse>(`/v1/sessions/${sessionId}`)
      if (r.session.status === 'deleted') return false
      applyTranscript(r, primaryModel)
      saveActiveSession({
        session_id: r.session.session_id,
        server_id: r.session.server_id,
        target_server: r.session.target_server,
      })
      if (isSessionRunning(r.session)) {
        setStreaming(true)
        setStreamingSince(Date.now())
        startPolling(r.session.session_id, primaryModel)
      } else {
        setStreaming(false)
        setStreamingSince(null)
      }
      return true
    } catch {
      return false
    }
  }

  useEffect(() => {
    apiFetch<PublicServer[]>('/v1/servers')
      .then((list) => {
        setServers(list)
        if (list.length && !serverPick) setServerPick(list[0].id)
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))

    apiFetch<PublicConfig>('/v1/config')
      .then((cfg) => {
        setConfig(cfg)
        setActiveModel((prev) => prev || cfg.model)
        void refreshSessions()
        const persisted = loadActiveSession()
        if (persisted) {
          void loadAndAttach(persisted.session_id, cfg.model).then((ok) => {
            if (!ok) clearActiveSession()
          })
        }
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [entries.length])

  useEffect(
    () => () => {
      abortActiveStream()
      stopPolling()
      stopTitlePolling()
    },
    [],
  )

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setRolodexOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const turns = useMemo(() => groupTurns(entries), [entries])
  const canStart = useMemo(() => serverPick !== null && !session, [serverPick, session])

  async function startSession() {
    setError(null)
    stopTitlePolling()
    try {
      const s = await apiFetch<SessionResponse>('/v1/sessions', {
        method: 'POST',
        body: JSON.stringify({ server_id: serverPick, title: '' }),
      })
      setSession(s)
      setEntries([])
      setAuditEvents([])
      setOpenDetailsTurns(new Set())
      if (config) setActiveModel(config.model)
      saveActiveSession({
        session_id: s.session_id,
        server_id: s.server_id,
        target_server: s.target_server,
      })
      await refreshSessions()
      requestAnimationFrame(() => textareaRef.current?.focus())
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  function goToNewSession() {
    abortActiveStream()
    stopPolling()
    stopTitlePolling()
    setSession(null)
    setEntries([])
    setAuditEvents([])
    setError(null)
    setStreaming(false)
    setStreamingSince(null)
    setOpenDetailsTurns(new Set())
    setFeedPanelOpen(false)
    if (config) setActiveModel(config.model)
    clearActiveSession()
  }

  async function switchSession(target: SessionResponse) {
    if (session && target.session_id === session.session_id) return
    abortActiveStream()
    stopPolling()
    stopTitlePolling()
    setEntries([])
    setAuditEvents([])
    setError(null)
    setStreaming(false)
    setStreamingSince(null)
    setFeedPanelOpen(false)
    const ok = await loadAndAttach(target.session_id, config?.model || '')
    if (!ok) {
      setError('could not open that session — it may have been deleted')
      await refreshSessions()
    }
  }

  async function pulpSession(target: SessionResponse) {
    const isCurrent = session && target.session_id === session.session_id
    try {
      await apiFetch(`/v1/sessions/${target.session_id}`, { method: 'DELETE' })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      return
    }
    if (isCurrent) {
      abortActiveStream()
      stopPolling()
      stopTitlePolling()
      setSession(null)
      setEntries([])
      setAuditEvents([])
      setStreaming(false)
      setStreamingSince(null)
      setOpenDetailsTurns(new Set())
      if (config) setActiveModel(config.model)
      clearActiveSession()
    }
    await refreshSessions()
  }

  async function recoverFromStreamDrop(sessionId: string, primaryModel: string) {
    const payload = await syncTranscript(sessionId, primaryModel, {
      resetDetails: false,
      refreshListOnIdle: true,
    })
    if (!payload) {
      setError('live stream interrupted and transcript reload failed')
      setStreaming(false)
      setStreamingSince(null)
      return
    }
    setError(null)
    if (isSessionRunning(payload.session)) {
      setStreaming(true)
      if (!streamingSince) setStreamingSince(Date.now())
      startPolling(sessionId, primaryModel)
    }
  }

  function handleEvent(ev: StreamEvent) {
    setEntries((prev) => {
      const next = [...prev]
      const id = Math.random().toString(36).slice(2)
      const ts = new Date().toISOString()
      switch (ev.event) {
        case 'assistant_delta': {
          const last = next[next.length - 1]
          if (last && last.kind === 'assistant') {
            last.text = (last.text || '') + ev.data.text
          } else {
            next.push({ id, ts, kind: 'assistant', text: ev.data.text })
          }
          break
        }
        case 'thinking_delta': {
          const last = next[next.length - 1]
          if (last && last.kind === 'thinking') {
            last.text = (last.text || '') + ev.data.text
          } else {
            next.push({ id, ts, kind: 'thinking', text: ev.data.text })
          }
          break
        }
        case 'tool_use': {
          const input = ev.data.input as { command?: string; description?: string }
          next.push({
            id,
            ts,
            kind: 'tool_use',
            command: input.command,
            description: input.description,
            toolName: ev.data.name,
          })
          break
        }
        case 'tool_result':
          {
            const parts = normalizeToolResultParts(ev.data.content)
            const text = ev.data.text || summarizeToolResultParts(parts)
            next.push({
              id,
              ts,
              kind: 'tool_result',
              text,
              parts,
              isError: ev.data.is_error,
              toolUseId: ev.data.tool_use_id,
              truncated: Boolean(ev.data.truncated),
            })
          }
          break
        case 'error':
          next.push({ id, ts, kind: 'error', text: ev.data.message })
          break
        case 'provider_fallback':
          next.push({
            id,
            ts,
            kind: 'provider_fallback',
            fromModel: ev.data.from_model,
            toModel: ev.data.to_model,
            reason: ev.data.reason,
          })
          setActiveModel(ev.data.to_model)
          break
        case 'done':
          next.push({
            id,
            ts,
            kind: 'done',
            cost: ev.data.cost_usd,
            stopReason: ev.data.stop_reason,
          })
          break
        case 'session':
          break
      }
      return next
    })
    if (ev.event === 'done' || ev.event === 'error') {
      setStreaming(false)
      setStreamingSince(null)
      streamRef.current = null
      let nextSessionId: string | null = null
      let shouldRefreshTitle = false
      setSession((prev) => {
        if (!prev) return prev
        nextSessionId = prev.session_id
        shouldRefreshTitle = !hasSessionTitle(prev)
        return {
          ...prev,
          turn_state: ev.event === 'done' ? 'idle' : prev.turn_state,
          turn_phase: 'none',
          current_activity: null,
          current_tool_name: null,
          current_command_preview: null,
          current_remote_pid: null,
        }
      })
      void refreshSessions()
      if (nextSessionId) {
        void syncTranscript(nextSessionId, config?.model || activeModel, {
          resetDetails: false,
          refreshListOnIdle: true,
        })
      }
      if (shouldRefreshTitle && nextSessionId) {
        refreshTitleSoon(nextSessionId, config?.model || activeModel)
      }
    }
  }

  async function send() {
    if (!session || !input.trim() || streaming) return
    const content = input.trim()
    setInput('')
    const ts = new Date().toISOString()
    setEntries((prev) => [
      ...prev,
      { id: Math.random().toString(36).slice(2), ts, kind: 'user', text: content },
    ])
    setStreaming(true)
    setStreamingSince(Date.now())
    setError(null)
    setStampKey(Date.now())
    startPolling(session.session_id, config?.model || activeModel, { immediate: false })
    streamAbortExpectedRef.current = false
    streamRef.current = streamTurn(session.session_id, content, handleEvent, (err) => {
      streamRef.current = null
      const aborted = err instanceof Error && err.name === 'AbortError'
      if (streamAbortExpectedRef.current || aborted) {
        streamAbortExpectedRef.current = false
        return
      }
      setError(err instanceof Error ? err.message : String(err))
      void recoverFromStreamDrop(session.session_id, config?.model || activeModel)
    })
  }

  async function forceStopSession(target?: SessionResponse) {
    const s = target || session
    if (!s || user?.role !== 'admin') return
    try {
      const next = await apiFetch<SessionResponse>(
        `/v1/sessions/${s.session_id}/force-stop`,
        { method: 'POST' },
      )
      if (session && next.session_id === session.session_id) {
        setSession(next)
        await loadAndAttach(next.session_id, config?.model || '')
      }
      await refreshSessions()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
      e.preventDefault()
      void send()
    } else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      void send()
    }
  }

  function toggleDetails(key: string) {
    setOpenDetailsTurns((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const currentBureau: PublicServer | null =
    servers.find((s) => s.id === (session?.server_id ?? serverPick ?? -1)) || null

  function toggleFeed() {
    if (!session) return
    if (window.matchMedia('(min-width: 1024px)').matches) {
      setFeedOpenPersisted(!feedOpen)
      return
    }
    setFeedPanelOpen((prev) => !prev)
  }

  const rolodexItems: RolodexItem[] = useMemo(() => {
    const actions: RolodexItem[] = [
      {
        id: 'action-new',
        group: 'ACTIONS',
        title: 'new session',
        meta: 'ctrl+n',
        onSelect: goToNewSession,
      },
    ]
    if (user?.role === 'admin') {
      actions.push({
        id: 'action-admin',
        group: 'ACTIONS',
        title: 'admin',
        meta: 'users + servers',
        onSelect: () => {
          window.location.href = '/admin'
        },
      })
    }
    const serverItems: RolodexItem[] = servers.map((s) => ({
      id: `server-${s.id}`,
      group: 'SERVERS',
      title: s.host,
      meta: s.role || undefined,
      onSelect: () => {
        setServerPick(s.id)
        if (!session) return
        goToNewSession()
      },
    }))
    const sessionItems: RolodexItem[] = sessions.map((s, idx) => ({
      id: `session-${s.session_id}`,
      group: 'SESSIONS',
      title: s.title || '(untitled)',
      meta: `${s.target_server} · #${(s.session_id.replace(/[^0-9a-f]/gi, '').slice(-4).toUpperCase() || String(idx).padStart(4, '0'))}`,
      onSelect: () => switchSession(s),
    }))
    return [...actions, ...serverItems, ...sessionItems]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servers, sessions, user, session])

  return (
    <AppShell
      bureau={
        session
          ? { host: session.target_server, role: currentBureau?.role }
          : currentBureau
            ? { host: currentBureau.host, role: currentBureau.role }
            : null
      }
      onOpenRolodex={() => setRolodexOpen(true)}
      onOpenSessions={() => setSessionsDrawerOpen(true)}
      menuExtras={
        session ? (
          <button
            onClick={toggleFeed}
            className="w-full text-left px-3 py-2 text-[14px] text-fg-soft hover:bg-surface hover:text-fg transition-colors flex items-center"
          >
            <span className="text-accent mr-2">&gt;</span>
            <span>live feed</span>
            <span className="ml-auto text-fg-dim text-[12px] uppercase tracking-[0.08em]">
              {feedOpen ? 'on' : 'off'}
            </span>
          </button>
        ) : undefined
      }
      modelLabel={activeModel}
      fallback={!!(config && activeModel && activeModel !== config.model)}
    >
      <div className="flex-1 flex min-h-0">
        <SessionList
          sessions={sessions}
          activeId={session?.session_id ?? null}
          onPickSession={switchSession}
          onNewSession={goToNewSession}
          onSpike={(s) => void forceStopSession(s)}
          onPulp={(s) => void pulpSession(s)}
          canSpike={user?.role === 'admin'}
          canPulp={user?.role === 'admin'}
          mobileOpen={sessionsDrawerOpen}
          onMobileClose={() => setSessionsDrawerOpen(false)}
        />

        <div className="flex-1 flex min-w-0 min-h-0">
          {!session ? (
            <div className="flex-1 overflow-y-auto flex items-center justify-center p-6">
              <div className="w-full max-w-md panel p-5 font-mono text-[14.5px] animate-fade-in">
                <div className="text-fg-dim text-[12px] uppercase tracking-[0.08em]">
                  // new session
                </div>
                <div className="mt-1 text-fg text-[15.5px]">Start a new session</div>
                <div className="mt-1 text-fg-mute text-[13px]">
                  Pick a server. The assistant will run commands on it over
                  SSH. Every message and command is logged.
                </div>

                <div className="mt-5">
                  <label className="block uppercase tracking-[0.08em] text-[11.5px] text-fg-mute mb-1.5">
                    server
                  </label>
                  {servers.length === 0 ? (
                    <div className="text-[12.5px] text-fg-dim">
                      no servers available.
                    </div>
                  ) : (
                    <select
                      value={serverPick ?? ''}
                      onChange={(e) => setServerPick(Number(e.target.value))}
                      className="input"
                    >
                      {servers.map((s) => (
                        <option key={s.id} value={s.id} className="bg-bg text-fg">
                          {s.host}
                          {s.role ? ` · ${s.role}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-end">
                  <button
                    onClick={startSession}
                    disabled={!canStart}
                    className="btn btn-primary"
                  >
                    <span className="text-fg-dim">[</span>
                    <span>start session</span>
                    <span className="text-[11px] text-fg-dim ml-1">↵</span>
                    <span className="text-fg-dim">]</span>
                  </button>
                </div>

                {error && (
                  <div className="mt-4 border-l-2 border-danger pl-3">
                    <div className="text-danger uppercase text-[11.5px] tracking-[0.08em]">
                      ✗ error
                    </div>
                    <div className="text-[14px] text-fg mt-0.5">{error}</div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 min-w-0 flex flex-col">
                <main className="flex-1 overflow-y-auto px-3 sm:px-5 md:px-8 py-5">
                  <div className="max-w-3xl mx-auto">
                    {/* Session header */}
                    <div className="pb-3 sm:pb-4 border-b border-line mb-4 sm:mb-5 animate-fade-in">
                      <div className="flex items-baseline gap-2 font-mono text-[11px] sm:text-[12px] text-fg-dim uppercase tracking-[0.08em]">
                        <StatusDot
                          tone={isSessionRunning(session) ? 'accent' : 'dim'}
                          pulse={isSessionRunning(session)}
                          size={7}
                        />
                        <span>session</span>
                        <span className="text-fg-dim normal-case tracking-normal">
                          #
                          {(session.session_id.replace(/[^0-9a-f]/gi, '').slice(-4).toUpperCase() ||
                            '0001').padStart(4, '0')}
                        </span>
                      </div>
                      <h2 className="mt-1 font-mono font-semibold text-fg text-[17px] sm:text-[20px] leading-tight break-words">
                        {session.title || '(untitled)'}
                      </h2>
                      <div className="mt-2 flex items-center gap-x-3 gap-y-1 flex-wrap font-mono text-[11.5px] sm:text-[12px] text-fg-dim tnum">
                        <span>
                          <span className="text-fg-mute">host:</span>{' '}
                          <span className="text-accent">{session.target_server}</span>
                        </span>
                        <span className="hidden sm:inline">·</span>
                        <span className="hidden sm:inline">
                          <span className="text-fg-mute">started:</span>{' '}
                          {new Date(session.created_at).toLocaleString(undefined, {
                            month: 'short',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        {user?.role === 'admin' && (
                          <>
                            <span className="hidden sm:inline">·</span>
                            <span className="hidden sm:inline">
                              <span className="text-fg-mute">by:</span>{' '}
                              {session.owner_email}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {turns.length === 0 && (
                      <div className="py-8 text-center font-mono text-[14px] text-fg-dim">
                        // no messages yet. type below to begin.
                      </div>
                    )}

                    <div className="space-y-6">
                      {turns.map((turn, idx) => {
                        const key = turnKey(turn)
                        const isLast = idx === turns.length - 1
                        return (
                          <TurnView
                            key={key}
                            turn={turn}
                            isStreaming={isLast && streaming}
                            showDetails={openDetailsTurns.has(key)}
                            onToggleDetails={() => toggleDetails(key)}
                            startedAtMs={isLast && streamingSince ? streamingSince : 0}
                            statusText={
                              isLast && streaming ? runtimeStatusText(session) : undefined
                            }
                          />
                        )
                      })}
                      {error && (
                        <div className="border-l-2 border-danger pl-3 font-mono">
                          <div className="text-danger uppercase text-[11.5px] tracking-[0.08em]">
                            ✗ error
                          </div>
                          <div className="mt-0.5 text-[14.5px] text-fg">{error}</div>
                        </div>
                      )}
                      <div ref={endRef} />
                    </div>
                  </div>
                </main>

                {/* Composer */}
                <footer className="border-t border-line bg-bg-raise shrink-0">
                  <div className="max-w-3xl mx-auto px-3 sm:px-5 md:px-8 py-3">
                    <div>
                      <div className="mb-1 flex min-h-[1rem] items-center justify-end">
                        {stampKey !== null && (
                          <StampFlourish
                            key={stampKey}
                            label={`sent · ${new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`}
                          />
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-3 mb-1.5 font-mono text-[11.5px]">
                        <div className="flex items-center gap-2 text-fg-dim uppercase tracking-[0.08em]">
                          <span>to:</span>
                          <span className="text-accent normal-case tracking-normal">
                            {session.target_server}
                          </span>
                        </div>
                        <span className="text-fg-dim">
                          ↵ send · ⇧↵ new line
                        </span>
                      </div>
                      <div className="panel-inset flex items-start gap-2 px-2.5 py-2 focus-within:border-accent transition-colors">
                        <span className="font-mono text-accent text-[15.5px] leading-[1.55] pt-0.5 shrink-0">
                          $
                        </span>
                        <textarea
                          ref={textareaRef}
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyDown={onKeyDown}
                          rows={2}
                          placeholder="ask the assistant to run something…"
                          className="flex-1 bg-transparent outline-none font-mono text-[15px] text-fg placeholder:text-fg-dim resize-y min-h-[2.2em]"
                        />
                      </div>
                      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {user?.role === 'admin' && (
                            <button
                              onClick={() => session && void pulpSession(session)}
                              className="btn"
                              aria-label="Delete this session"
                            >
                              <span className="text-fg-dim">[</span>
                              <span>rm</span>
                              <span className="text-fg-dim">]</span>
                              <span className="text-fg-mute">session</span>
                            </button>
                          )}
                          {user?.role === 'admin' && isSessionRunning(session) && (
                            <button
                              onClick={() => void forceStopSession()}
                              className="btn btn-danger"
                              aria-label="Force stop this session"
                            >
                              <span className="text-fg-dim">[</span>
                              <span>kill</span>
                              <span className="text-fg-dim">]</span>
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-3 ml-auto flex-wrap">
                          {streaming && (
                            <span className="font-mono text-[12px] text-fg-mute inline-flex items-center gap-2">
                              <StatusDot tone="accent" pulse size={7} />
                              {runtimeStatusText(session) || 'running…'}
                            </span>
                          )}
                          <button
                            onClick={send}
                            disabled={!input.trim() || streaming}
                            className="btn btn-primary"
                          >
                            <span className="text-fg-dim">[</span>
                            <span>send</span>
                            <span className="text-[11px] text-fg-dim ml-1">↵</span>
                            <span className="text-fg-dim">]</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </footer>
              </div>

              {feedOpen && (
                <aside className="hidden lg:flex w-[28rem] max-w-[42vw] border-l border-line min-h-0 bg-bg-raise">
                  <LiveFeed items={feedItems} session={session} streaming={streaming} />
                </aside>
              )}
            </>
          )}
        </div>
      </div>

      <SidePanel
        open={!!session && feedPanelOpen}
        onClose={() => setFeedPanelOpen(false)}
        title="live feed"
        subtitle={session ? `#${session.session_id.replace(/[^0-9a-f]/gi, '').slice(-4).toUpperCase()}` : undefined}
        width="w-[420px]"
      >
        <div className="h-[calc(100vh-110px)] min-h-0 -mx-5 -my-5">
          <LiveFeed items={feedItems} session={session} streaming={streaming} />
        </div>
      </SidePanel>

      <Rolodex
        open={rolodexOpen}
        onClose={() => setRolodexOpen(false)}
        items={rolodexItems}
      />
    </AppShell>
  )
}
